import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DxChartModule } from 'devextreme-angular';
import { AggregateType, ThemeService } from '@services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimeInterval } from 'devextreme-angular/common/charts';

export interface BlockedStockSeriesConfig {
    valueField: string;
    name: string;
    color: string;
}

@Component({
    selector: 'app-blocked-stock-chart',
    imports: [DxChartModule],
    templateUrl: './blocked-stock-chart.component.html',
    styles: [
        `
            dx-chart {
                height: 300px;
                width: 100%;
            }

            ::ng-deep dx-chart .dxc-markers rect {
                cursor: pointer;
            }
        `,
    ]
})
export class BlockedStockChartComponent implements OnInit {
    private themeService = inject(ThemeService);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);

    @Input() title: string = '';
    @Input() data: any[] | null = [];
    @Input() series: BlockedStockSeriesConfig[] = [];
    @Input() formatPercent: boolean = true;
    @Input() targetValue: number | null = null;
    @Input() targetLabel: string = '';
    @Input() wholeRange: { startValue?: Date; endValue?: Date } = {};
    @Input() visualRange: { startValue?: Date; endValue?: Date } = {};
    @Input() aggregateType: AggregateType = AggregateType.Day;

    targetColor: string = '';

    get isStacked(): boolean {
        return this.series.length > 1;
    }

    get tickInterval(): TimeInterval {
        switch (this.aggregateType) {
            case AggregateType.Week:
                return 'week';
            case AggregateType.Month:
                return 'month';
            default:
                return 'day';
        }
    }

    ngOnInit(): void {
        this.themeService.isDark$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.targetColor = getComputedStyle(document.body)
                    .getPropertyValue('--bs-danger').trim();
            });
    }

    formatPercentText = (info: any) => `${(info.value * 100).toFixed(1)}%`;

    formatLabelText = (info: any): string =>
        this.formatPercent
            ? `${(info.value * 100).toFixed(1)}%`
            : (info.value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    customizeTooltip = (info: any) => ({
        text: this.formatLabelText(info)
    });

    onPointClick = async (e: any) => {
        const data = e.target?.data;
        if (!data?.minDate || !data?.maxDate) return;

        await this.router.navigate(['/inventory/blocked-stock/data'], {
            queryParams: {
                from: data.minDate.toISOString(),
                to: data.maxDate.toISOString()
            }
        });
    };
}
